// src/pages/Fruits.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  Search,
  FilterList,
  ShoppingCart,
  WhatsApp,
  Info,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { fruitsAPI } from '../services/api';
import toast from 'react-hot-toast';
import FruitCard from '../components/Fruits/FruitCard';

const Fruits = () => {
  const [fruits, setFruits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const search = params.get('search');
    if (search) {
      setSearchTerm(search);
    }
    fetchCategories();
    fetchFruits();
  }, [location.search, page, category]);

  const fetchCategories = async () => {
    try {
      const response = await fruitsAPI.getCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchFruits = async () => {
    try {
      setLoading(true);
      const params = {
        search: searchTerm,
        category: category || undefined,
      };
      
      const response = await fruitsAPI.getAll(params);
      setFruits(response.data);
      setTotalPages(Math.ceil(response.data.length / 12));
    } catch (error) {
      console.error('Error fetching fruits:', error);
      toast.error('Failed to load fruits');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (category) params.set('category', category);
    navigate(`/fruits?${params.toString()}`);
  };

  const handleClear = () => {
    setSearchTerm('');
    setCategory('');
    navigate('/fruits');
  };

  const displayedFruits = fruits.slice((page - 1) * 12, page * 12);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" gutterBottom>
          Browse Fruits
        </Typography>
        <Typography variant="h6" color="text.secondary" paragraph>
          Explore our collection of premium quality fruits from around the world
        </Typography>
      </Box>

      {/* Search and Filter */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <form onSubmit={handleSearch}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search fruits, varieties, origins..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={category}
                  label="Category"
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                type="submit"
                startIcon={<FilterList />}
                sx={{ height: '56px' }}
              >
                Apply Filters
              </Button>
            </Grid>
          </Grid>
        </form>
        
        {(searchTerm || category) && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Active filters:
            </Typography>
            {searchTerm && (
              <Chip
                label={`Search: ${searchTerm}`}
                onDelete={() => setSearchTerm('')}
                size="small"
              />
            )}
            {category && (
              <Chip
                label={`Category: ${category}`}
                onDelete={() => setCategory('')}
                size="small"
              />
            )}
            <Button
              size="small"
              onClick={handleClear}
              sx={{ ml: 'auto' }}
            >
              Clear All
            </Button>
          </Box>
        )}
      </Paper>

      {/* Fruits Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : fruits.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No fruits found. Try different search terms.
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
            {displayedFruits.map((fruit) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={fruit.id}>
                <FruitCard fruit={fruit} />
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default Fruits;